import React from 'react';
import { Fab, useTheme, useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { createConversation } from '../redux/actions/conversationAction';
import { setDocument, setSelectedCitation } from '../redux/actions/documentAction';
import ReactGA from 'react-ga4';
import { tracker } from '../analytics';
import { useTranslation } from 'react-i18next';

const FloatingActionButton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const user = useSelector(state => state.auth?.user?.user);

  // Define the main pages where FAB should be shown
  const mainPages = [
    '/', // Dashboard
    '/search',
    '/conversation',
    '/contract'
  ];

  // Check if current path is a main page
  const isMainPage = mainPages.some(page => 
    location.pathname === page || 
    (page === '/' && location.pathname === '/dashboard')
  );

  const handleCreateConversation = async () => {
    try {
      const newConversation = await dispatch(createConversation(user.id));
      if (newConversation) {
        dispatch(setDocument(null));
        dispatch(setSelectedCitation(null));
        if (!isMobile) {
          enqueueSnackbar(t("create_conversation_success"), { variant: 'success' });
        }
        navigate(`/conversation/${newConversation.id}`);

        ReactGA.event({
          category: 'Conversation',
          action: 'User created a new conversation',
          label: `User ID: ${user.id}`
        });
        tracker.event({
          category: 'Conversation',
          action: 'User created a new conversation',
          label: `User ID: ${user.id}`
        });
      }
    } catch (error) {
      enqueueSnackbar('Failed to create conversation', { variant: 'error' });
    }
  };

  // Only show on mobile and main pages
  if (!isMobile || !isMainPage) return null;

  return (
    <Fab
      color="primary"
      aria-label="create conversation"
      onClick={handleCreateConversation}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        background: 'linear-gradient(135deg, #2196f3 0%, #4aa7ff 100%)',
        boxShadow: '0 4px 8px rgba(33, 150, 243, 0.4)',
        '&:hover': {
          background: 'linear-gradient(135deg, #1e88e5 0%, #42a5f5 100%)',
          boxShadow: '0 6px 12px rgba(33, 150, 243, 0.5)',
          transform: 'translateY(-2px)'
        },
        transition: 'all 0.2s ease'
      }}
    >
      <AddIcon />
    </Fab>
  );
};

export default FloatingActionButton; 